import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translateWithVars } from '../utils/i18n/translateWithVars';
import { useRole, useRoleModal } from '../contexts/RoleContext';
import { AlertTriangle, Clock, Lock, Key } from 'lucide-react';
import lockoutService from '../services/lockoutService';
import { useLoadingState } from '../hooks/useSharedState';
import { getCurrentBusinessId } from '../utils/sharedCore';
import AdsModalPortal from './ads/AdsModalPortal';
import { usePresenceTransition } from '../hooks/usePresenceTransition';
import { AdsBackButton, AdsButton } from './ads';
import { isBusinessLogoutInProgress } from '../utils/businessLogoutFlag';
import '../styles/components/code-entry-modal.css';

const CodeEntryModal = () => {
  const { t, isRTL } = useLanguage();
  const tCodeEntryModal = (key, vars) => translateWithVars(t(`codeEntryModal.${key}`), vars);
  const { loginWithCode, currentRole, logoutToLoginPage } = useRole();
  const { showCodeModal, setShowCodeModal } = useRoleModal();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const { isLoading, withLoading } = useLoadingState();
  const [lockoutStatus, setLockoutStatus] = useState(null);
  const [showUnlockCodeField, setShowUnlockCodeField] = useState(false);
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockCodeError, setUnlockCodeError] = useState('');
  const [isValidatingUnlockCode, setIsValidatingUnlockCode] = useState(false);
  const errorClearTimerRef = useRef(null);

  // Never show Manager-Code over Accueil during / after business logout
  const roleRequired = !currentRole;
  const shouldRenderCodeModal =
    Boolean(showCodeModal) &&
    !isBusinessLogoutInProgress();

  useEffect(() => () => {
    if (errorClearTimerRef.current) clearTimeout(errorClearTimerRef.current);
  }, []);

  // Poll lockout only while the modal is relevant — not every 5s for the whole session.
  useEffect(() => {
    if (!shouldRenderCodeModal) return undefined;

    let interval = null;
    let isMounted = true;

    const checkLockoutStatus = () => {
      if (isMounted) {
        setLockoutStatus(lockoutService.getLockoutStatus());
      }
    };

    checkLockoutStatus();
    interval = setInterval(checkLockoutStatus, 5000);

    return () => {
      isMounted = false;
      if (interval) clearInterval(interval);
    };
  }, [shouldRenderCodeModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockoutStatus?.isLocked) return;

    if (!code.trim()) {
      setError(tCodeEntryModal('errCodeRequired'));
      return;
    }

    setError('');

    const result = await withLoading(async () => loginWithCode(code.trim()));

    if (result.success) {
      setCode('');
      setError('');
      if (result.requiresCashConfirmation) return;
    } else {
      const errorMessage = result.message || result.error || tCodeEntryModal('errInvalidCode');
      setError(errorMessage);
      if (!result.isDeactivated) {
        setLockoutStatus(lockoutService.getLockoutStatus());
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !lockoutStatus?.isLocked) {
      handleSubmit(e);
    }
  };

  const handleUnlockCodeSubmit = async (e) => {
    e.preventDefault();

    if (!unlockCode.trim()) {
      setUnlockCodeError(tCodeEntryModal('errUnlockCodeRequired'));
      return;
    }

    setIsValidatingUnlockCode(true);
    setUnlockCodeError('');

    try {
      const businessId = getCurrentBusinessId();
      if (!businessId) {
        setUnlockCodeError(tCodeEntryModal('errBusinessId'));
        return;
      }

      const result = await lockoutService.validateUnlockCode(businessId, unlockCode.trim());

      if (result.success) {
        setUnlockCode('');
        setUnlockCodeError('');
        setShowUnlockCodeField(false);
        setLockoutStatus(lockoutService.getLockoutStatus());
        setError(tCodeEntryModal('successUnlock'));
        if (errorClearTimerRef.current) clearTimeout(errorClearTimerRef.current);
        errorClearTimerRef.current = setTimeout(() => {
          setError('');
          errorClearTimerRef.current = null;
        }, 3000);
      } else {
        setUnlockCodeError(result.message || tCodeEntryModal('errInvalidUnlockCode'));
      }
    } catch {
      setUnlockCodeError(tCodeEntryModal('errUnlockValidation'));
    } finally {
      setIsValidatingUnlockCode(false);
    }
  };

  const { present, closing } = usePresenceTransition(Boolean(shouldRenderCodeModal));
  if (!present) return null;

  const overlayExitClass = closing ? ' ads-modal-exiting' : ' ads-modal-entering';

  if (lockoutStatus?.isLocked) {
    return (
      <AdsModalPortal>
      <div
        className={`modal-overlay cem-lockout-overlay${overlayExitClass}`}
        dir={isRTL ? 'rtl' : 'ltr'}
        data-ads-modal-state={closing ? 'closing' : 'open'}
      >
        <div className="modal-content cem-lockout-content">
          <div className="cem-lockout-icon">
            <Lock size={32} />
          </div>
          <h2 className="cem-lockout-title">{tCodeEntryModal('lockoutTitle')}</h2>
          <p className="cem-lockout-message">{tCodeEntryModal('lockoutMessage')}</p>

          <div className="cem-lockout-timer">
            <div className="cem-lockout-timer-header">
              <Clock size={18} className="cem-icon-gap" />
              <span className="cem-lockout-timer-label">{tCodeEntryModal('timeRemaining')}</span>
            </div>
            <div className="cem-lockout-timer-value">
              {lockoutStatus?.remaining?.minutes?.toString().padStart(2, '0')}:
              {lockoutStatus?.remaining?.seconds?.toString().padStart(2, '0')}
            </div>
          </div>

          <div className="cem-lockout-attempts">
            <AlertTriangle size={14} className="cem-icon-gap" />
            {tCodeEntryModal('failedAttempts', { count: lockoutStatus?.attempts })}
          </div>

          <div className="cem-emergency-section">
            <p className="cem-emergency-text">{tCodeEntryModal('emergencyUnlockHint')}</p>
            {!showUnlockCodeField ? (
              <AdsButton
                type="button"
                variant="primary"
                onClick={() => setShowUnlockCodeField(true)}
                className="cem-emergency-toggle-btn"
              >
                <Key size={14} className="cem-icon-gap" aria-hidden />
                {tCodeEntryModal('enterEmergencyCode')}
              </AdsButton>
            ) : (
              <form onSubmit={handleUnlockCodeSubmit}>
                <input
                  type="text"
                  value={unlockCode}
                  onChange={(e) => setUnlockCode(e.target.value.toUpperCase())}
                  placeholder={tCodeEntryModal('emergencyCodePlaceholder')}
                  maxLength={8}
                  className="cem-emergency-input"
                  autoFocus
                />
                {unlockCodeError && <div className="cem-emergency-error">{unlockCodeError}</div>}
                <div className="cem-emergency-actions">
                  <AdsButton type="submit" variant="success" disabled={isValidatingUnlockCode} className="cem-unlock-submit-btn">
                    {isValidatingUnlockCode ? tCodeEntryModal('validating') : tCodeEntryModal('unlockBtn')}
                  </AdsButton>
                  <AdsButton
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowUnlockCodeField(false);
                      setUnlockCode('');
                      setUnlockCodeError('');
                    }}
                    className="cem-unlock-cancel-btn"
                  >
                    {tCodeEntryModal('cancel')}
                  </AdsButton>
                </div>
              </form>
            )}
          </div>

          {!roleRequired ? (
            <AdsButton type="button" variant="secondary" onClick={() => setShowCodeModal(false)} className="cem-lockout-close-btn">
              {tCodeEntryModal('close')}
            </AdsButton>
          ) : (
            <AdsBackButton className="cem-back-btn" onClick={logoutToLoginPage}>
              {tCodeEntryModal('backToLogin')}
            </AdsBackButton>
          )}
        </div>
      </div>
      </AdsModalPortal>
    );
  }

  return (
    <AdsModalPortal>
    <div
      className={`modal-overlay cem-overlay${overlayExitClass}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-ads-modal-state={closing ? 'closing' : 'open'}
    >
      <div className="modal-content cem-content cem-content--compact">
        {roleRequired && (
          <AdsBackButton
            className="cem-back-btn cem-back-btn--top"
            onClick={logoutToLoginPage}
          >
            {tCodeEntryModal('backToLogin')}
          </AdsBackButton>
        )}

        {!roleRequired && (
          <AdsButton type="button" variant="ghost" size="sm" onClick={() => setShowCodeModal(false)} className="cem-dismiss-btn" aria-label={tCodeEntryModal('close')}>
            ×
          </AdsButton>
        )}

        <h2 className="cem-title">{tCodeEntryModal('enterCodeTitle')}</h2>

        <form onSubmit={handleSubmit} className="cem-form">
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={tCodeEntryModal('codePlaceholder')}
            disabled={lockoutStatus?.isLocked}
            className={`cem-input${lockoutStatus?.isLocked ? ' cem-input--locked' : ''}`}
            autoFocus={!lockoutStatus?.isLocked}
            aria-label={tCodeEntryModal('codeLabel')}
          />

          {error && <div className="cem-form-error">{error}</div>}

          <AdsButton
            type="submit"
            variant="primary"
            disabled={isLoading || lockoutStatus?.isLocked}
            className="cem-submit-btn"
          >
            {isLoading
              ? tCodeEntryModal('verifying')
              : lockoutStatus?.isLocked
                ? tCodeEntryModal('systemLocked')
                : tCodeEntryModal('loginBtn')}
          </AdsButton>
        </form>
      </div>
    </div>
    </AdsModalPortal>
  );
};

export default CodeEntryModal;
